import { router, useLocalSearchParams } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BrandTile } from '@/components/BrandTile';
import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Input } from '@/components/Input';
import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import { formatUsPhoneDisplay, isValidEmail, normalizeUsPhone } from '@/lib/contact';
import { getPendingReferral } from '@/lib/pendingReferral';
import { API_ERROR_CODE, isApiError, RateLimitError } from '@/services/errors';
import { authStore } from '@/stores/auth.store';

type Channel = 'sms' | 'email';

const LoginScreen = observer(() => {
  // Auth-flow-redesign: signup may redirect here when the form's
  // contact matched an existing User (`outcome: "existing"`). We
  // pre-fill the input and surface a soft "you already have an
  // account" banner so the user doesn't have to type their contact
  // twice or wonder why their submit landed on log-in.
  const params = useLocalSearchParams<{
    prefill_contact?: string;
    banner?: string;
    redirect?: string;
  }>();
  const prefillContact = typeof params.prefill_contact === 'string' ? params.prefill_contact : null;
  const showExistingAccountBanner = params.banner === 'existing_account';
  // Deep link the user was trying to reach (set by auth-gated layouts).
  // Threaded through to verify so the PIN flow lands them back there.
  const redirect = typeof params.redirect === 'string' ? params.redirect : undefined;
  // Phones come in as E.164 (+15551234567); emails contain "@".
  const initialChannel: Channel = useMemo(
    () => (prefillContact?.includes('@') ? 'email' : 'sms'),
    [prefillContact],
  );

  const [channel, setChannel] = useState<Channel>(initialChannel);
  // Strip the E.164 `+1` so `formatUsPhoneDisplay` (which treats input
  // as 10 digits + country code stripped) renders "(555) 123-4567"
  // instead of "(155) 512-3456".
  const [phoneRaw, setPhoneRaw] = useState(
    prefillContact && !prefillContact.includes('@')
      ? prefillContact.replace(/^\+1/, '')
      : '',
  );
  const [email, setEmail] = useState(
    prefillContact && prefillContact.includes('@') ? prefillContact : '',
  );
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Set only when the carrier rejected the send outright, which is the one
  // case where retrying this number is pointless and email is the way in.
  const [undeliverablePhone, setUndeliverablePhone] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);
  // Whether a referral is already cached. If yes, the user came in
  // through a `?r=<token>` link and we skip the "click the link again"
  // nudge. If no (cold install / direct app open), we surface F7's note
  // so referred users don't lose attribution. May-5 addition.
  const [hasPendingReferral, setHasPendingReferral] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPendingReferral()
      .then((ref) => {
        if (!cancelled) setHasPendingReferral(ref !== null);
      })
      .catch(() => {
        if (!cancelled) setHasPendingReferral(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (rateLimitSeconds <= 0) return;
    const id = setInterval(() => {
      setRateLimitSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [rateLimitSeconds]);

  const isSubmittable = (() => {
    if (submitting) return false;
    if (rateLimitSeconds > 0) return false;
    if (channel === 'sms') return normalizeUsPhone(phoneRaw) !== null;
    return isValidEmail(email);
  })();

  // Clears the recovery and hands the form back. Also clears the phone field:
  // leaving a number we KNOW is undeliverable sitting in the input is what let
  // "Send code" re-run the identical failure.
  function resetUndeliverable() {
    setUndeliverablePhone(null);
    setErrorMessage(null);
    setPhoneRaw('');
  }

  // Hands them to the email field that already exists on this screen. The
  // login door has no committed account yet, so there is nothing to look an
  // address up FROM — the verify screen's "email the address on file" escape
  // only works because by then a code was already sent to a known phone.
  // Reusing the shipped toggle keeps one email path, not two.
  function switchToEmail() {
    setUndeliverablePhone(null);
    setErrorMessage(null);
    setChannel('email');
  }

  async function handleSubmit() {
    if (!isSubmittable) return;
    setSubmitting(true);
    setErrorMessage(null);
    setUndeliverablePhone(null);
    const normalizedPhone = channel === 'sms' ? normalizeUsPhone(phoneRaw) : null;
    try {
      if (channel === 'sms' && !normalizedPhone) {
        setErrorMessage('That phone number looks invalid. Please double-check.');
        return;
      }
      const contact = channel === 'sms' ? normalizedPhone! : email.trim();
      await authStore.requestPin(channel, contact);
      router.push({
        pathname: '/(auth)/verify',
        params: redirect ? { contact, channel, redirect } : { contact, channel },
      });
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitSeconds(err.retryAfterSeconds);
        setErrorMessage(`Too many requests. Try again in ${err.retryAfterSeconds}s.`);
      } else if (isApiError(err) && err.code === 'invalid_contact') {
        setErrorMessage('That looks invalid. Double-check and try again.');
      } else if (isApiError(err) && err.code === API_ERROR_CODE.Undeliverable && normalizedPhone) {
        // The carrier refused this number outright, so every retry fails the
        // same way. That is not a form error and does not get form-error
        // treatment — the recovery panel replaces the form entirely, because
        // the form is finished for this number.
        setErrorMessage(err.message);
        setUndeliverablePhone(normalizedPhone);
      } else if (isApiError(err)) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <View style={styles.root}>
      <Image
        source={require('@/assets/images/athlete-bg.jpg')}
        style={styles.bg}
        resizeMode="cover"
      />
      <View style={styles.overlay} />

      <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.flex}
        >
          <ScrollView
            contentContainerStyle={styles.scroll}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.cardWrap}>
              <View style={styles.card}>
                <View style={styles.brand}>
                  <BrandTile size={56} combo="frostOnDeep" />
                  <Heading size="4xl" style={styles.heading}>Sign in</Heading>
                  <Text style={styles.tagline}>Your worth, worth more.</Text>
                  <Text style={styles.subTagline}>
                    Enter your phone or email and we&apos;ll send a code.
                  </Text>
                </View>

                {showExistingAccountBanner && (
                  <View style={styles.existingAccountBanner}>
                    <Text style={styles.existingAccountBannerText}>
                      Looks like you already have an account — sign in to continue.
                    </Text>
                  </View>
                )}

                {hasPendingReferral === false && !showExistingAccountBanner && (
                  <View style={styles.referralHint}>
                    <Text style={styles.referralHintText}>
                      Referred by someone? Open their link again — signing in here won&apos;t credit them.
                    </Text>
                  </View>
                )}

                <View style={styles.field}>
                  {channel === 'sms' ? (
                    <>
                      <Input
                        // Distinct keys on the two branches are load-bearing.
                        // Both render an <Input> as the first child of the same
                        // parent, so without them React reconciles them as ONE
                        // element and only updates props — the native TextInput
                        // is never remounted. iOS does not re-apply
                        // keyboardType to a live (especially focused) input, so
                        // switching to email left the phone-pad on screen: no
                        // letter keys, and typing an email silently did nothing.
                        // Reported twice from the field, 2026-08-09.
                        key="phone"
                        label="Phone number"
                        focused={phoneFocused}
                        value={formatUsPhoneDisplay(phoneRaw)}
                        onChangeText={setPhoneRaw}
                        onFocus={() => setPhoneFocused(true)}
                        onBlur={() => setPhoneFocused(false)}
                        placeholder="(555) 123-4567"
                        keyboardType="phone-pad"
                        autoComplete="tel"
                        textContentType="telephoneNumber"
                        maxLength={14}
                      />
                      {/* Hidden while the recovery is up: its primary button
                          carries the same label and the same action, and two
                          identical controls one above the other is a coin
                          flip, not a choice. */}
                      {!undeliverablePhone && (
                        <Button
                          label="Use email instead"
                          variant="ghost"
                          size="sm"
                          style={styles.toggle}
                          onPress={() => setChannel('email')}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Input
                        // See the phone branch — this key is what forces a
                        // fresh TextInput (and therefore the right keyboard).
                        key="email"
                        label="Email"
                        focused={emailFocused}
                        value={email}
                        onChangeText={setEmail}
                        onFocus={() => setEmailFocused(true)}
                        onBlur={() => setEmailFocused(false)}
                        placeholder="you@school.edu"
                        keyboardType="email-address"
                        autoComplete="email"
                        textContentType="emailAddress"
                        autoCapitalize="none"
                      />
                      <Button
                        label="Use phone instead"
                        variant="ghost"
                        size="sm"
                        style={styles.toggle}
                        onPress={() => setChannel('sms')}
                      />
                    </>
                  )}
                </View>

                {errorMessage && !undeliverablePhone && (
                  <Text style={styles.error}>{errorMessage}</Text>
                )}

                {/*
                  A carrier rejection is not a form error, so it does not get
                  form-error treatment. This number will never receive our
                  texts — retrying is guaranteed to fail — so the recovery
                  TAKES OVER from the form rather than being appended under
                  it, and "Send code" is suppressed entirely while it is up.
                  Left as a footnote it ranked below the consent legalese,
                  under a green button still inviting the failed action.

                  Two exits on purpose, because the server cannot tell us
                  which one applies without revealing whether the number has
                  an account (the enumeration leak this whole feature is
                  built around). Email works if they have one; Sign up is
                  the answer if they don't and would otherwise wait forever
                  for a message nobody sent.
                */}
                {undeliverablePhone && (
                  <View style={styles.recovery}>
                    <Text style={styles.recoveryTitle}>We couldn&apos;t text that number</Text>
                    <Text style={styles.recoveryBody}>
                      That number can’t receive our texts, so sending again won’t help. Sign in
                      with your email instead, or try a different number.
                    </Text>

                    <Button
                      label="Use email instead"
                      variant="primary"
                      fullWidth
                      onPress={switchToEmail}
                      style={styles.recoveryPrimary}
                    />

                    {/* Sign-up is deliberately NOT duplicated here — the
                        "New here? Sign up" row below stays visible, and it is
                        the answer for anyone who has no account to email. */}
                    <Button
                      label="Use a different number"
                      variant="ghost"
                      size="sm"
                      onPress={resetUndeliverable}
                      style={styles.recoveryAlt}
                    />
                  </View>
                )}

                {/*
                  Both are suppressed while the recovery is up: "Send code"
                  would re-run a send the carrier has already refused, and the
                  SMS consent line is describing a text that cannot arrive.
                  The recovery panel carries its own exits.
                */}
                {!undeliverablePhone && (
                  <>
                    <Button
                      label={rateLimitSeconds > 0 ? `Try again in ${rateLimitSeconds}s` : 'Send code'}
                      variant="primary"
                      fullWidth
                      disabled={!isSubmittable}
                      loading={submitting}
                      onPress={handleSubmit}
                      style={styles.submit}
                    />

                    <Text style={styles.consent}>
                      By continuing, you agree to receive a one-time code via{' '}
                      {channel === 'sms' ? 'SMS' : 'email'}. Standard rates apply. Reply STOP to
                      opt out.
                    </Text>
                  </>
                )}

                <View style={styles.signUpRow}>
                  <Text style={styles.signUpPrompt}>New here?</Text>
                  <Button
                    label="Sign up"
                    variant="ghost"
                    size="sm"
                    onPress={() => router.push('/(auth)/signup')}
                  />
                </View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
});

export default LoginScreen;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.deepGreen },
  bg: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.deepGreenOverlay,
  },
  safe: { flex: 1 },
  flex: { flex: 1 },
  scroll: { flexGrow: 1, justifyContent: 'center', padding: spacing.lg },
  cardWrap: { width: '100%', alignItems: 'center' },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: colors.frost,
    borderRadius: radii.lg,
    padding: spacing.xl,
    ...shadows.modal,
  },
  brand: { alignItems: 'center', marginBottom: spacing.xl, gap: spacing.base },
  heading: { textAlign: 'center' },
  tagline: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.base,
    color: colors.deepGreen,
  },
  subTagline: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 16,
  },
  referralHint: {
    backgroundColor: colors.frost,
    borderColor: colors.electricGreen,
    borderWidth: 1,
    borderRadius: radii.base,
    padding: spacing.base,
    marginBottom: spacing.lg,
  },
  referralHintText: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
    lineHeight: 18,
  },
  existingAccountBanner: {
    backgroundColor: colors.frost,
    borderColor: colors.electricGreen,
    borderWidth: 1,
    borderRadius: radii.base,
    padding: spacing.base,
    marginBottom: spacing.lg,
  },
  existingAccountBannerText: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
    lineHeight: 18,
  },
  signUpRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.base,
    gap: spacing.xs,
  },
  signUpPrompt: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textMuted,
  },
  field: { marginBottom: spacing.lg, gap: spacing.sm },
  toggle: { alignSelf: 'flex-start' },
  error: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.danger,
    marginBottom: spacing.base,
  },
  // Borrows the existing bordered-callout pattern (see existingAccountBanner)
  // so the recovery reads as brand furniture rather than a bolted-on error.
  recovery: {
    backgroundColor: colors.frost,
    borderColor: colors.electricGreen,
    borderWidth: 1,
    borderRadius: radii.base,
    padding: spacing.base,
    marginBottom: spacing.base,
  },
  recoveryTitle: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.base,
    color: colors.deepGreen,
    marginBottom: spacing.xs,
  },
  recoveryBody: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    color: colors.text,
    lineHeight: 22,
    marginBottom: spacing.base,
  },
  recoveryPrimary: { marginBottom: spacing.xs },
  recoveryAlt: { alignSelf: 'center' },
  submit: { marginBottom: spacing.base },
  consent: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 18,
  },
});
